import { AssignmentTypeEnum } from 'api/model';
import { personalTransportAssignmentTypes } from 'constants/AppConstants';
import { calculateHaversineDistance } from 'utils/geo-distance';

/**
 * Frontend-spegling av backend-hjälparen ElectricCarBufferHelper. Beräknar den
 * automatiska laddningsbufferten (ms) som läggs på ett elbilsuppdrags estimerade tid:
 * 0 om körsträckan är kortare än 10 km fågelvägen, annars +30 min inom län / +1 h
 * mellan län, 0 för placeholder/personlig transport. Används dels för att VISA
 * "+30 min (elbil)" / "+1 h (elbil)" bredvid estimerad tid, dels för att skicka
 * electricCarBufferMs till fälttestar-sorteringens körpolicy-/tillgänglighetskoll vid
 * planering (innan sparning). Servern äger det persisterade värdet
 * (Assignment.electricCarBufferTime) och räknar om det vid varje spar.
 *
 * Avståndsregeln kräver från- OCH till-koordinater. Klienten får dem från persisterade
 * uppdrag och — via useCompanyCoordinateResolver — från geotaggen på de företag adresserna
 * pekar på, vilket är samma steg 1 som backends AssignmentCoordinateResolver. Ingen
 * geokodning sker i webbläsaren, så fritextadresser och ogeotaggade företag saknar
 * koordinater och förhandsvisningen faller tillbaka på länsregeln — servern kan då landa
 * på 0 efter geokodningen. Hellre visa en buffert för mycket än släppa den på gissning.
 */
export const ELECTRIC_CAR_WITHIN_COUNTY_BUFFER_MS = 30 * 60 * 1000;
export const ELECTRIC_CAR_CROSS_COUNTY_BUFFER_MS = 60 * 60 * 1000;
/** Kortare körsträcka än så här (fågelvägen, meter) ⇒ ingen buffert. Strikt "kortare än". */
export const ELECTRIC_CAR_SHORT_DISTANCE_THRESHOLD_METERS = 10_000;

/** Koordinatparen som avståndsregeln behöver — strukturellt kompatibelt med AssignmentModel. */
export interface ElectricCarBufferCoordinates {
  fromLatitude?: number | null;
  fromLongitude?: number | null;
  toLatitude?: number | null;
  toLongitude?: number | null;
}

// Uppdragstyper utan bil att ladda: placeholder + den delade listan över personliga
// transporter (samma lista som resten av admin-webben — ingen tredje handkopierad kopia).
// Typad som number[] så anropare kan skicka både AssignmentTypeEnum och råa number-värden
// (t.ex. Number(state[...])) utan nominell typkonflikt mellan NSwag- och Orval-enumen.
const NON_VEHICLE_ASSIGNMENT_TYPES: number[] = [
  AssignmentTypeEnum.CarPlaceholder,
  ...personalTransportAssignmentTypes,
];

/**
 * true = kortare än 10 km fågelvägen, false = 10 km eller längre,
 * null = minst en koordinat saknas (avståndet kan inte avgöras).
 */
export const isElectricCarShortDistance = (
  coordinates: ElectricCarBufferCoordinates | undefined
): boolean | null => {
  if (
    coordinates?.fromLatitude == null ||
    coordinates.fromLongitude == null ||
    coordinates.toLatitude == null ||
    coordinates.toLongitude == null
  ) {
    return null;
  }
  const meters = calculateHaversineDistance(
    coordinates.fromLatitude,
    coordinates.fromLongitude,
    coordinates.toLatitude,
    coordinates.toLongitude
  );
  return meters < ELECTRIC_CAR_SHORT_DISTANCE_THRESHOLD_METERS;
};

/** Största manuella buffert TL kan välja — speglar backendens MaxManualBufferMs (3 h). */
export const ELECTRIC_CAR_MAX_MANUAL_BUFFER_MS = 180 * 60 * 1000;

/** Valen i buffert-dropdownen: 0, 15, 30 … 180 min. */
export const ELECTRIC_CAR_MANUAL_BUFFER_OPTIONS_MS: number[] = Array.from(
  { length: ELECTRIC_CAR_MAX_MANUAL_BUFFER_MS / (15 * 60 * 1000) + 1 },
  (_, i) => i * 15 * 60 * 1000
);

/** Om uppdraget alls kan ha en laddbuffert: elbilsärende och ett ben med bil att ladda. */
export const isElectricCarBufferApplicable = (
  isElectric: boolean,
  assignmentType: number | undefined
): boolean =>
  isElectric &&
  !(
    assignmentType !== undefined &&
    NON_VEHICLE_ASSIGNMENT_TYPES.includes(assignmentType)
  );

/**
 * Laddbufferten (ms). `manualBufferMs` är ett TL-valt (eller tidigare sparat) värde
 * (null/undefined eller negativt = regeln) och ersätter avstånds-/länsregeln — men gäller,
 * precis som i backend, bara när bufferten alls är tillämplig (elbil, ej
 * placeholder/personlig transport).
 */
export const getElectricCarBufferMs = (
  isElectric: boolean,
  assignmentType: number | undefined,
  fromCountyId: number | null | undefined,
  toCountyId: number | null | undefined,
  coordinates?: ElectricCarBufferCoordinates,
  manualBufferMs?: number | null
): number => {
  if (!isElectricCarBufferApplicable(isElectric, assignmentType)) return 0;
  if (manualBufferMs != null && manualBufferMs >= 0) {
    return Math.min(manualBufferMs, ELECTRIC_CAR_MAX_MANUAL_BUFFER_MS);
  }
  // Kort körsträcka ⇒ ingen laddning behövs. Okända koordinater (null) faller igenom till
  // länsregeln — speglar backend.
  if (isElectricCarShortDistance(coordinates) === true) return 0;
  if (fromCountyId == null || toCountyId == null) return 0;
  return fromCountyId === toCountyId
    ? ELECTRIC_CAR_WITHIN_COUNTY_BUFFER_MS
    : ELECTRIC_CAR_CROSS_COUNTY_BUFFER_MS;
};

/** "0 min", "45 min", "1 h", "1 h 15 min" … */
export const formatElectricCarBuffer = (bufferMs: number): string => {
  const totalMinutes = Math.round(bufferMs / (60 * 1000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} min`;
  return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
};

/**
 * Returnerar en kort etikett för bufferten, eller null om ingen buffert gäller. Med
 * `showZero` visas även 0 ("+0 min (elbil)") — används där etiketten är klickbar, så att
 * TL kan lägga på en buffert även när regeln gav 0.
 */
export const getElectricCarBufferLabel = (
  bufferMs: number,
  showZero = false
): string | null => {
  if (bufferMs <= 0 && !showZero) return null;
  return `+${formatElectricCarBuffer(Math.max(0, bufferMs))} (elbil)`;
};
